import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import memoize from 'react-utils/memoize';
import styled from 'styled-components';
import { EXTRA_LARGE_CLOSE_BUTTON_SIZE, MEDIUM_CLOSE_BUTTON_SIZE, MODAL_CLOSE_BUTTON_OFFSET, MODAL_CLOSE_BUTTON_SIZE, SMALL_CLOSE_BUTTON_SIZE } from 'HubStyleTokens/sizes';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { toPx } from '../utils/Styles';
import UIDialogButton from '../dialog/UIDialogButton';
const SIZE_OPTIONS = {
  sm: {
    size: SMALL_CLOSE_BUTTON_SIZE,
    padding: 12
  },
  md: {
    size: MEDIUM_CLOSE_BUTTON_SIZE,
    padding: 16
  },
  lg: {
    size: MODAL_CLOSE_BUTTON_SIZE,
    padding: 20
  },
  xl: {
    size: EXTRA_LARGE_CLOSE_BUTTON_SIZE,
    padding: 38
  }
};

/*
 * $size and $padding should be treated as "transient props," but styled-components has some optimizations that cause these props to still be passed to `UIDialogButton`.
 * Also tried using the `shouldForwardProp` config method, but that wasn't working either (likely due to the same optimizations in styled-components).
 * Since these props ($size and $padding) are just used to style the `CloseButton`, the props are thrown away in `UIDialogButton` so that they aren't passed to the DOM element as attributes.
 */
const CloseButton = styled(UIDialogButton).withConfig({
  displayName: "UICloseButton__CloseButton",
  componentId: "kqo0i6-0"
})(["position:absolute;right:", ";top:", ";width:", ";height:", ";color:", ";&::after{padding:", ";}"], MODAL_CLOSE_BUTTON_OFFSET, MODAL_CLOSE_BUTTON_OFFSET, ({
  $size
}) => toPx($size), ({
  $size
}) => toPx($size), ({
  color
}) => color, ({
  $padding
}) => toPx($padding));
const CloseIcon = styled.svg.withConfig({
  displayName: "UICloseButton__CloseIcon",
  componentId: "kqo0i6-1"
})(["color:inherit;display:block;flex-grow:1;"]);
const CloseIconInner = styled.path.withConfig({
  displayName: "UICloseButton__CloseIconInner",
  componentId: "kqo0i6-2"
})(["fill:currentColor;stroke:currentColor;stroke-width:2;"]);
const renderCloseIcon = memoize(() => /*#__PURE__*/_jsx(CloseIcon, {
  viewBox: "0 0 14 14",
  xmlns: "http://www.w3.org/2000/svg",
  children: /*#__PURE__*/_jsx(CloseIconInner, {
    d: "M14.5,1.5l-13,13m0-13,13,13",
    transform: "translate(-1 -1)"
  })
}));

const UICloseButton = (_ref) => {
  let {
    className,
    disabled = false,
    size = 'lg'
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["className", "disabled", "size"]);

  const sizeOptions = SIZE_OPTIONS[toShorthandSize(size)];
  return /*#__PURE__*/_jsx(CloseButton, Object.assign({}, rest, {
    "aria-label": I18n.text('salesUI.UICloseButton.label'),
    className: classNames('private-close__button', className),
    "data-action": "close",
    disabled: disabled // @ts-expect-error This is incorrect usage of the IconHolder prop, but it seems to work fine
    ,
    IconHolder: renderCloseIcon,
    $padding: sizeOptions.padding,
    $size: sizeOptions.size
  }));
};

UICloseButton.propTypes = {
  color: PropTypes.string,
  disabled: PropTypes.bool,
  size: propTypeForSizes(['sm', 'md', 'lg', 'xl'])
};
UICloseButton.defaultProps = {
  disabled: false,
  size: 'lg'
};
UICloseButton.displayName = 'UICloseButton';
export default UICloseButton;